import { TabsComponent, type TabItem } from './tabs';
import { SummaryContent } from './summary/summary-content';
import { SkillsAssessment } from './skill-assessment/skills-assessment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TranscriptAnalysis } from './transcript/transcript-analysis';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '@/utils/ky';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCustomLanguageFromURL, updateCustomLanguageInURL, getLanguageConfig } from '@/utils/customLanguageUtils';

export function RoleplayFeedback() {
  const navigate = useNavigate();
  const queryString = new URLSearchParams(window.location.search).toString();
  const { t, i18n } = useTranslation();

  // Language detection and setup
  const currentLanguage = getCustomLanguageFromURL();
  const languageConfig = getLanguageConfig(currentLanguage);

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [transcript, setTranscript] = useState<any>(null);
  const [skillsAssessment, setSkillsAssessment] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [characterName, setCharacterName] = useState<string | undefined>(null);

  // Set i18n language when component mounts
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  const { mutate: getTranscriptAnalysis } = useMutation({
    mutationFn: (data: { sessionId: string }) =>
      api
        .post('realtime/api/transcript-analysis', {
          json: data,
          timeout: 60000,
        })
        .json<{
          success: boolean;
          transcriptAnalysis: any;
          characterName: string | undefined;
        }>(),
    onSuccess: (data) => {
      console.log('Transctipt analysis: ', data);
      setTranscript(data.transcriptAnalysis);
      setCharacterName(data.characterName);
    },
    onError: (error: any) => {
      toast.error(t('custom.feedback.errors.transcriptAnalysis'));
    },
  });

  const { mutate: getSkillsAssessment } = useMutation({
    mutationFn: (data: { sessionId: string }) =>
      api
        .post('realtime/api/skills-assessment', {
          json: data,
          timeout: 60000,
        })
        .json<{ success: boolean; skillsAssessment: any }>(),
    onSuccess: (data) => {
      console.log('Skills assessment: ', data);
      setSkillsAssessment(data.skillsAssessment);
    },
    onError: (error: any) => {
      toast.error(t('custom.feedback.errors.skillsAssessment'));
    },
  });

  const { mutate: getSummary } = useMutation({
    mutationFn: (data: { sessionId: string }) =>
      api
        .post('realtime/api/roleplay/summary', {
          json: data,
          timeout: 60000,
        })
        .json<{ success: boolean; summary: any }>(),
    onSuccess: (data) => {
      console.log('Summary: ', data);
      setSummary(data.summary);
    },
    onError: (error: any) => {
      toast.error(t('custom.feedback.errors.summary'));
    },
  });

  useEffect(() => {
    if (sessionId) {
      getTranscriptAnalysis({ sessionId });
      getSkillsAssessment({ sessionId });
      getSummary({ sessionId });
    }
  }, [sessionId]);

  const tabs: TabItem[] = [
    {
      value: 'summary',
      label: t('custom.feedback.tabs.summary'),
      content: <SummaryContent summary={summary} />,
    },
    {
      value: 'skills-assessment',
      label: t('custom.feedback.tabs.skillsAssessment'),
      content: <SkillsAssessment assessments={skillsAssessment} />,
    },
    {
      value: 'transcript',
      label: t('custom.feedback.tabs.transcriptAnalysis'),
      content: (
        <TranscriptAnalysis
          transcript={transcript}
          characterName={characterName}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <div className="bg-white border-b border-[#e0e0e0]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/favicon.ico"
              alt={t('custom.feedback.header.iconAlt')}
              className="h-6 w-6"
            />
            <h1 className="text-lg font-medium">{t('custom.feedback.header.title')}</h1>
          </div>
          <button className="block p-2 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => navigate(-1)}>
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
          <TabsComponent tabs={tabs} defaultValue="summary" />
        </div>
      </div>
    </div>
  );
}
